/**
 * Brand color constants, matching the repo-root DESIGN.md (the canonical brand
 * palette). Components use the
 * Vuetify theme slots and SCSS tokens built from these, never the literals.
 */
import {MD_COLORS} from '@s/material_colors';

interface IBrandBlueFamily {
   /** Hover / pressed shade of the standard brand blue. */
   deep: string;
   /** Standard brand blue ("Trello blue") — light-theme primary. */
   std: string;
   /** Bright brand blue — dark-theme primary and emphasis. */
   strong: string;
   /** Hover / pressed shade of the strong blue (dark-theme hover). */
   strongDeep: string;
   lighter: string;
   light: string;
   pale: string;
}

interface IBrandAccentFamily {
   std: string;
   light: string;
}

interface IBrandTealFamily extends IBrandAccentFamily {
   /** Hover / pressed shade of the standard teal. */
   deep: string;
}

interface IBrandNeutrals {
   n50: string;
   n100: string;
   n200: string;
   n300: string;
   n400: string;
   n500: string;
   n600: string;
   n700: string;
   n800: string;
   n900: string;
   n950: string;
}

interface IBrandColors {
   blue: IBrandBlueFamily;
   teal: IBrandTealFamily;
   violet: IBrandAccentFamily;
   /** Middle stop of the signature hero gradient (decorative use only). */
   royal: IBrandAccentFamily;
   /** Cool blue-gray neutral ramp (replaces the Material grey scale). */
   neutral: IBrandNeutrals;
}

/**
 * A value supplied per theme. Resolve to the active theme's member with
 * `pickThemeVariant()` (or the `ThemePaletteMixin` in components).
 */
export interface IThemeVariant<T> {
   /** Value used by the light theme. */
   light: T;
   /** Value used by the dark theme. */
   dark: T;
}

interface IStatusColors {
   info: IThemeVariant<string>;
   success: IThemeVariant<string>;
   warning: IThemeVariant<string>;
   error: IThemeVariant<string>;
}

interface ISurfaceColors {
   background: IThemeVariant<string>;
   surface: IThemeVariant<string>;
   surfaceLight: IThemeVariant<string>;
   text: IThemeVariant<string>;
   selection: IThemeVariant<string>;
}

export const AB_BRAND: IBrandColors = {
   blue: {
      deep: '#0b5aa0',
      std: '#0967b3',
      strong: '#2b96ee',
      strongDeep: '#1b7fd6',
      lighter: '#5aa6e6',
      light: '#8fbee8',
      pale: '#bfdcf5',
   },
   teal: {
      std: '#28bdb1',
      light: '#5cd3c8',
      deep: '#219e94',
   },
   violet: {
      std: '#573cdd',
      light: '#8a7fe6',
   },
   royal: {
      std: '#1152d4',
      light: '#4f7df5',
   },
   neutral: {
      n50: '#f7f9fc',
      n100: '#edf1f8',
      n200: '#dfe4ed',
      n300: '#c5cedb',
      n400: '#92a1b7',
      n500: '#677487',
      n600: '#4f5a6d',
      n700: '#3f4a5a',
      n800: '#2c3542',
      n900: '#19222f',
      n950: '#10161f',
   },
};

/** Status colors per theme. Not from the website brand; tune freely. */
export const AB_STATUS: IStatusColors = {
   info: {light: AB_BRAND.blue.strong, dark: AB_BRAND.blue.lighter},
   success: {light: '#059669', dark: '#34d399'},
   warning: {light: '#d97706', dark: '#fbbf24'},
   error: {light: '#dc2626', dark: '#f87171'},
};

/**
 * Canvas/surface/text/selection values per theme. Surface architecture: white
 * content ground with tinted chrome (surfaceLight) in light mode; darkest-flat
 * content with lighter chrome in dark mode.
 */
export const AB_SURFACES: ISurfaceColors = {
   background: {light: '#ffffff', dark: '#14181f'},
   surface: {light: '#ffffff', dark: '#1d222a'},
   surfaceLight: {light: '#eceff1', dark: AB_BRAND.neutral.n800},
   text: {light: AB_BRAND.neutral.n900, dark: '#e6ebf1'},
   selection: {light: AB_BRAND.blue.pale, dark: '#1c3d5c'},
};

/**
 * Vuetify 4's stock theme colors, kept as a record of what the brand themes
 * replace. Unreferenced on purpose; do not delete as unused.
 *
 * Matches vuetify@4.1.3 `genDefaults()`; re-check on upgrade:
 * https://github.com/vuetifyjs/vuetify/blob/v4.1.3/packages/vuetify/src/composables/theme.ts
 */
export const VUETIFY_DEFAULTS = {
   light: {
      background: '#FFFFFF',
      surface: '#FFFFFF',
      'surface-bright': '#FFFFFF',
      'surface-light': '#EEEEEE',
      'surface-variant': '#424242',
      'on-surface-variant': '#EEEEEE',
      primary: '#1867C0',
      'primary-darken-1': '#1F5592',
      secondary: '#48A9A6',
      'secondary-darken-1': '#018786',
      error: '#B00020',
      info: '#2196F3',
      success: '#4CAF50',
      warning: '#FB8C00',
   },
   dark: {
      background: '#121212',
      surface: '#212121',
      'surface-bright': '#ccbfd6',
      'surface-light': '#424242',
      'surface-variant': '#c8c8c8',
      'on-surface-variant': '#000000',
      primary: '#2196F3',
      'primary-darken-1': '#277CC1',
      secondary: '#54B6B2',
      'secondary-darken-1': '#48A9A6',
      error: '#CF6679',
      info: '#2196F3',
      success: '#4CAF50',
      warning: '#FB8C00',
   },
} as const;

/**
 * Return the color as #RRGGBBAA at the given opacity (0 transparent, 1 opaque).
 * Accepts #RGB or #RRGGBB, with or without the #; throws when the length is
 * not 3 or 6 (characters are not validated).
 *
 * @example
 * alphaColor('#ABC', 0.5)    // '#AABBCC80'
 */
export function alphaColor(color: string, transparency: number): string {
   const hex = color.replace('#', '');

   const alpha = Math.round(transparency * 255)
      .toString(16)
      .padStart(2, '0');

   if (hex.length === 3) {
      const r = hex[0];
      const g = hex[1];
      const b = hex[2];
      return `#${r}${r}${g}${g}${b}${b}${alpha}`;
   }

   if (hex.length === 6) {
      return `#${hex}${alpha}`;
   }

   throw new Error('Invalid color format. Must be #RGB or #RRGGBB');
}

/**
 * Return the color as an "r, g, b" triplet, the form Vuetify uses for its
 * --v-theme-* variables. Accepts #RGB or #RRGGBB; throws on anything else.
 *
 * @example
 * rgbTriple('#0967b3')            // '9, 103, 179'
 * `rgba(${rgbTriple(hex)}, 0.5)`  // palette color at 50% alpha
 */
export function rgbTriple(color: string): string {
   const hex = color.replace('#', '');
   const full_hex = hex.length === 3 ? [...hex].map((ch) => ch + ch).join('') : hex;

   // Non-hex characters would parse to NaN; reject them here.
   if (!/^[0-9a-fA-F]{6}$/.test(full_hex)) {
      throw new Error('Invalid color format. Must be #RGB or #RRGGBB');
   }

   const r = Number.parseInt(full_hex.slice(0, 2), 16);
   const g = Number.parseInt(full_hex.slice(2, 4), 16);
   const b = Number.parseInt(full_hex.slice(4, 6), 16);
   return `${r}, ${g}, ${b}`;
}

/**
 * Return the light or dark member of a themed value. Components use
 * `ThemePaletteMixin`, which reads the live theme; call this only from code
 * that already knows which theme is active.
 */
export function pickThemeVariant<T>(variant: IThemeVariant<T>, isDark: boolean): T {
   return isDark ? variant.dark : variant.light;
}

export interface IPaletteLevel {
   /** Background color for chips, rows, highlights. */
   bg_color: string;
   /** Accent/line color for chart lines, borders, icons. */
   accent: string;
   /** Foreground/text color to render on top of bg_color. */
   text: string;
   /** Chip background color (where distinct from bg_color). */
   chip?: string;
}

/**
 * Build a light/dark IPaletteLevel pair from a Material color ramp (chip tuning A,
 * decided 2026-09-07): light is lighten_4 surface with darken_4 text and lighten_3
 * chip fill; dark mirrors as darken_4 / lighten_4 / darken_3. Accents are passed
 * per theme.
 */
export function themedLevel(opts: {
   variants: typeof MD_COLORS.red.color;
   lightAccent: string;
   darkAccent: string;
   withChip?: boolean;
}): IThemeVariant<IPaletteLevel> {
   const {variants, lightAccent, darkAccent, withChip} = opts;

   const light: IPaletteLevel = {
      bg_color: variants.lighten_4,
      accent: lightAccent,
      text: variants.darken_4,
   };
   const dark: IPaletteLevel = {
      bg_color: variants.darken_4,
      accent: darkAccent,
      text: variants.lighten_4,
   };

   if (withChip) {
      light.chip = variants.lighten_3;
      dark.chip = variants.darken_3;
   }

   return {light, dark};
}

/** Entity swatch colors (boards, labels, member avatars); picked by index or by id hash. */
// The MD steps nearest old Trello's board palette; deep-purple-400 is the
// uncustomized-board default.
export const BOARD_SWATCH_COLORS: readonly string[] = [
   '#0288d1', // light-blue-700
   '#f57c00', // orange-700
   '#689f38', // light-green-700
   '#d84315', // deep-orange-800
   '#7e57c2', // deep-purple-400 — the default canvas
   '#ec407a', // pink-400
   '#66bb6a', // green-400
   '#00acc1', // cyan-600
   '#78909c', // blue-grey-400
];

/**
 * Data-display palettes (status chips, priority markers, rating scales),
 * separate from the brand theme slots. Each entry is a light/dark
 * IPaletteLevel pair; resolve with `ThemePaletteMixin` or pickThemeVariant().
 */
export const APP_PALETTE: {
   stopLight: Record<'stop' | 'caution' | 'go', IThemeVariant<IPaletteLevel>>;
   workflowStatus: Record<
      'success' | 'pending' | 'warning' | 'error' | 'neutral' | 'info' | 'hold' | 'draft',
      IThemeVariant<IPaletteLevel>
   >;
   priority: Record<'critical' | 'high' | 'medium' | 'normal' | 'low', IThemeVariant<IPaletteLevel>>;
   ratingScale: Record<
      'rating1' | 'rating2' | 'rating3' | 'rating4' | 'rating5' | 'empty',
      IThemeVariant<IPaletteLevel>
   >;
} = {
   /** Simple stop light indicator. */
   stopLight: {
      stop: themedLevel({
         variants: MD_COLORS.red.color,
         lightAccent: MD_COLORS.red.color.base,
         darkAccent: MD_COLORS.red.color.lighten_1,
      }),
      caution: themedLevel({
         variants: MD_COLORS.amber.color,
         lightAccent: MD_COLORS.amber.color.base,
         darkAccent: MD_COLORS.amber.color.lighten_1,
      }),
      go: themedLevel({
         variants: MD_COLORS.green.color,
         lightAccent: MD_COLORS.green.color.base,
         darkAccent: MD_COLORS.green.color.lighten_1,
      }),
   },

   /** Workflow / record status indicators. */
   workflowStatus: {
      success: themedLevel({
         variants: MD_COLORS.green.color,
         lightAccent: MD_COLORS.green.color.darken_1,
         darkAccent: MD_COLORS.green.color.lighten_1,
         withChip: true,
      }),
      pending: themedLevel({
         variants: MD_COLORS.amber.color,
         lightAccent: MD_COLORS.amber.color.darken_1,
         darkAccent: MD_COLORS.amber.color.lighten_1,
         withChip: true,
      }),
      warning: themedLevel({
         variants: MD_COLORS.orange.color,
         lightAccent: MD_COLORS.orange.color.darken_1,
         darkAccent: MD_COLORS.orange.color.lighten_1,
         withChip: true,
      }),
      error: themedLevel({
         variants: MD_COLORS.red.color,
         lightAccent: MD_COLORS.red.color.darken_1,
         darkAccent: MD_COLORS.red.color.lighten_1,
         withChip: true,
      }),
      neutral: themedLevel({
         variants: MD_COLORS.grey.color,
         lightAccent: MD_COLORS.grey.color.darken_1,
         darkAccent: MD_COLORS.grey.color.lighten_1,
         withChip: true,
      }),
      info: themedLevel({
         variants: MD_COLORS.blue.color,
         lightAccent: MD_COLORS.blue.color.darken_1,
         darkAccent: MD_COLORS.blue.color.lighten_1,
         withChip: true,
      }),
      hold: themedLevel({
         variants: MD_COLORS.deep_purple.color,
         lightAccent: MD_COLORS.deep_purple.color.darken_1,
         darkAccent: MD_COLORS.deep_purple.color.lighten_1,
         withChip: true,
      }),
      draft: themedLevel({
         variants: MD_COLORS.cyan.color,
         lightAccent: MD_COLORS.cyan.color.darken_1,
         darkAccent: MD_COLORS.cyan.color.lighten_1,
         withChip: true,
      }),
   },

   /** Priority levels, critical (highest) through low. */
   priority: {
      critical: themedLevel({
         variants: MD_COLORS.red.color,
         lightAccent: MD_COLORS.red.color.darken_1,
         darkAccent: MD_COLORS.red.color.lighten_1,
         withChip: true,
      }),
      high: themedLevel({
         variants: MD_COLORS.orange.color,
         lightAccent: MD_COLORS.orange.color.darken_1,
         darkAccent: MD_COLORS.orange.color.lighten_1,
         withChip: true,
      }),
      medium: themedLevel({
         variants: MD_COLORS.purple.color,
         lightAccent: MD_COLORS.purple.color.darken_1,
         darkAccent: MD_COLORS.purple.color.lighten_1,
         withChip: true,
      }),
      normal: themedLevel({
         variants: MD_COLORS.blue.color,
         lightAccent: MD_COLORS.blue.color.darken_1,
         darkAccent: MD_COLORS.blue.color.lighten_1,
         withChip: true,
      }),
      low: themedLevel({
         variants: MD_COLORS.grey.color,
         lightAccent: MD_COLORS.grey.color.darken_1,
         darkAccent: MD_COLORS.grey.color.lighten_1,
         withChip: true,
      }),
   },

   /** 5-point rating scale, red for 1 through green for 5. */
   ratingScale: {
      rating1: themedLevel({
         variants: MD_COLORS.red.color,
         lightAccent: MD_COLORS.red.color.base,
         darkAccent: MD_COLORS.red.color.lighten_1,
      }),
      rating2: themedLevel({
         variants: MD_COLORS.orange.color,
         lightAccent: MD_COLORS.orange.color.base,
         darkAccent: MD_COLORS.orange.color.lighten_1,
      }),
      rating3: themedLevel({
         variants: MD_COLORS.blue.color,
         lightAccent: MD_COLORS.blue.color.base,
         darkAccent: MD_COLORS.blue.color.lighten_1,
      }),
      rating4: themedLevel({
         variants: MD_COLORS.lime.color,
         lightAccent: MD_COLORS.lime.color.darken_2,
         darkAccent: MD_COLORS.lime.color.lighten_1,
      }),
      rating5: themedLevel({
         variants: MD_COLORS.green.color,
         lightAccent: MD_COLORS.green.color.base,
         darkAccent: MD_COLORS.green.color.lighten_1,
      }),
      empty: themedLevel({
         variants: MD_COLORS.grey.color,
         lightAccent: MD_COLORS.grey.color.base,
         darkAccent: MD_COLORS.grey.color.lighten_1,
      }),
   },
};
